@if (isLoading()) {
  <lib-overlay-loader />
} @else if (scheduleDetails(); as details) {
  <div class="bg-white rounded-lg flex flex-col h-full">
    <header class="mb-xl">
      <div class="mb-2xl">
        <a (click)="goBack()" (keydown.enter)="goBack()" tabindex="0">
          <lib-chevron-down-icon
            [iconClass]="'size-10 text-neutral-900 rotate-90 p-sm rounded-lg border border-neutral-200 cursor-pointer'" />
        </a>
      </div>

      <div
        class="flex items-center justify-between gap-md border-b border-neutral-200 pb-md">
        <div class="flex items-center gap-md">
          <h5 class="text-2xl font-semibold text-neutral-900">
            #{{ details.id }}
          </h5>

          @if (details.state) {
            <div class="w-[1px] bg-neutral-200 px-xs h-6"></div>
            <lib-badge
              [label]="details.state.label"
              [badgeClass]="
                'px-md py-xs border-none text-sm font-medium text-white ' +
                (details.state.value | status)
              " />
          }
        </div>

        @if (actionsList().length) {
          <lib-action-list
            [menuItems]="actionsList()"
            (itemClick)="onActionClick($event)" />
        }
      </div>
    </header>

    <div class="flex flex-col gap-2xl">
      @if (isExpandCollapseSectionsEnabled()) {
        <lib-expand-collapse-all
          [(isAllSectionsExpanded)]="isAllSectionsExpanded" />
      }

      <lib-section-header
        [title]="'schedule_workflow_screen_section_title' | transloco"
        [isAccordion]="isExpandCollapseSectionsEnabled()"
        [isAccordionOpen]="isAllSectionsExpanded()"
        [contentTemplate]="scheduleInfoContentTemplate">
        <ng-template #scheduleInfoContentTemplate>
          <div
            class="grid [grid-template-columns:repeat(auto-fill,minmax(20rem,1fr))] gap-lg">
            @if (details.created_by) {
              <div>
                <p class="view-mode-label">
                  {{ 'schedule_details_creator_label' | transloco }}
                </p>
                <div class="view-mode-value flex items-center gap-sm">
                  <lib-avatar
                    [size]="'list'"
                    [fullName]="details.created_by.name"
                    [img]="details.created_by.image?.url ?? ''"
                    borderRadius="rounded-full" />
                  <span class="text-sm font-medium text-neutral-900">
                    {{ details.created_by.name }}
                  </span>
                </div>
              </div>
            }

            @if (details.start_at) {
              <div>
                <p class="view-mode-label">
                  {{
                    'schedule_workflow_form_start_datetime_label' | transloco
                  }}
                </p>
                <p
                  class="view-mode-value break-word text-neutral-900"
                  dir="ltr">
                  {{
                    (details.start_at | date: 'dd-MMM-yyyy hh:mm a') +
                      timeZoneView
                  }}
                </p>
              </div>
            }

            @if (scheduleTypeDisplay(); as scheduleType) {
              <div>
                <p class="view-mode-label">
                  {{ 'schedule_workflow_form_type_label' | transloco }}
                </p>
                <p class="view-mode-value break-word text-neutral-900">
                  {{ scheduleType.label }}
                </p>
              </div>
            }

            @if (byDisplay(); as by) {
              <div>
                <p class="view-mode-label">
                  {{ 'event_monthly_months_label' | transloco }}
                </p>
                <p class="view-mode-value break-word text-neutral-900">
                  {{ by }}
                </p>
              </div>
            }

            @if (intervalDisplay(); as interval) {
              <div>
                <p class="view-mode-label">
                  {{ 'schedule_workflow_form_repeat_every_label' | transloco }}
                </p>
                <p class="view-mode-value break-word text-neutral-900">
                  {{ interval }}
                </p>
              </div>
            }

            @if (monthsDisplay(); as months) {
              <div>
                <p class="view-mode-label">
                  {{ 'event_monthly_months_select_label' | transloco }}
                </p>
                <p class="view-mode-value break-word text-neutral-900">
                  {{ months }}
                </p>
              </div>
            }

            @if (daysDisplay(); as days) {
              <div>
                <p class="view-mode-label">
                  {{ 'schedule_workflow_form_days_label' | transloco }}
                </p>
                <p class="view-mode-value break-word text-neutral-900">
                  {{ days }}
                </p>
              </div>
            }

            @if (repeatOnDisplay(); as repeatOn) {
              <div>
                <p class="view-mode-label">
                  {{ 'schedule_workflow_form_repeat_on_label' | transloco }}
                </p>
                <p class="view-mode-value break-word text-neutral-900">
                  {{ repeatOn }}
                </p>
              </div>
            }

            @if (details.end_at) {
              <div>
                <p class="view-mode-label">
                  {{ 'schedule_workflow_form_end_datetime_label' | transloco }}
                </p>
                <p
                  class="view-mode-value break-word text-neutral-900"
                  dir="ltr">
                  {{
                    (details.end_at | date: 'dd-MMM-yyyy hh:mm a') +
                      timeZoneView
                  }}
                </p>
              </div>
            }

            <div class="md:col-span-full">
              <p class="view-mode-label">
                {{ 'schedule_workflow_form_description_label' | transloco }}
              </p>
              @if (details.description) {
                <div class="view-mode-value text-neutral-900">
                  <lib-long-text-view [content]="details.description" />
                </div>
              } @else {
                <p class="view-mode-value break-word text-neutral-900">
                  {{ 'details_not_found_label' | transloco }}
                </p>
              }
            </div>
          </div>
        </ng-template>
      </lib-section-header>

      @if (isScheduleRequestsEnabled()) {
        <lib-section-header
          [title]="'schedule_details_requests_section_title' | transloco"
          [isAccordion]="isExpandCollapseSectionsEnabled()"
          [isAccordionOpen]="isAllSectionsExpanded()"
          [contentTemplate]="scheduleRequestsContentTemplate">
          <ng-template #scheduleRequestsContentTemplate>
            <lib-schedule-requests
              [scheduleId]="scheduleId()"
              [permissions]="userPermissions()" />
          </ng-template>
        </lib-section-header>
      }
    </div>
  </div>
}

<ng-template #cancelScheduleModal>
  <lib-delete-confirmation-popup
    [deleteItemLabels]="{
      deleteItemPopupTitle: 'cancel_schedule_modal_title',
      deleteItemPopupDescription: 'cancel_schedule_modal_description',
      deleteItemPopupYesButton: 'cancel_schedule_modal_confirm_button',
      deleteItemPopupNoButton: 'cancel_schedule_modal_cancel_button',
    }"
    [isDeleteItemLoading]="isCancelling()"
    (deleteItem)="confirmCancelSchedule()"
    (closeModal)="closeCancelScheduleModal()" />
</ng-template>
